/**
 * The Review screen's list, as pure functions: what one row carries, which
 * filter it sits under, and one page of it. The server builds the rows from
 * Firestore (api/_lib/recipeReviewQueue.ts); the screen re-derives a row's
 * state with the same functions after every answer, so a row leaves its filter
 * the moment it is settled, without a reload.
 */
import { contentGaps, rowState } from './recipeMetadataReview';
import { isTimeSource, type TimeSource } from './recipeTimings';
import { isTimelineStatus, type RecipeTimeline, type TimelineStatus } from './recipeTimeline';
import type {
  ContentGap,
  MetadataReview,
  MetadataSuggestion,
  ReviewScope,
  ReviewSource,
  RowState,
} from '../types/recipeMetadata';

export type ReviewFilter = 'to-check' | 'looks-right' | 'ready-to-publish' | 'done';
export const REVIEW_FILTERS: readonly ReviewFilter[] = ['to-check', 'looks-right', 'ready-to-publish', 'done'];

export type ReviewSourceFilter = 'all' | ReviewSource;
export const REVIEW_SOURCE_FILTERS: readonly ReviewSourceFilter[] = ['all', 'import', 'library', 'catalogue'];

export const REVIEW_PAGE_SIZE = 50;

/** One row: the recipe's tags and review, never its ingredients or steps (the gaps say what is missing). */
export interface ReviewRow {
  id: string;
  scope: ReviewScope;
  source: ReviewSource;
  state: RowState;
  gaps: ContentGap[];
  title: string;
  image?: string;
  sourceUrl?: string;
  importBatchTitle?: string;
  importedVia?: string;
  savedAt?: string;
  reviewStatus?: string;
  cuisines: unknown[];
  dishTypes: unknown[];
  diets: unknown[];
  metadataSuggestion?: MetadataSuggestion;
  metadataReview?: MetadataReview;
  readyInMinutes?: number;
  preparationMinutes?: number;
  cookingMinutes?: number;
  timeSource?: TimeSource;
  /** The step timeline's state (#850): `review` carries the disputed timeline and why. */
  handsOnMinutes?: number;
  timelineStatus?: TimelineStatus;
  timelineSuggestion?: RecipeTimeline;
  timelineChecks?: string[];
}

export function filterOf(state: RowState): ReviewFilter {
  switch (state) {
    case 'ready-to-publish': return 'ready-to-publish';
    case 'looks-right': return 'looks-right';
    case 'done': return 'done';
    default: return 'to-check'; // blocked, to-check, ready: each needs a person.
  }
}

const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);
const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

/** Saved recipes still in the import flow. Anything else saved is the library. */
export function sourceOfSaved(doc: { reviewStatus?: unknown }): ReviewSource {
  return doc.reviewStatus === 'pendingReview' || doc.reviewStatus === 'approved' ? 'import' : 'library';
}

/**
 * Builds a row from a Firestore document. `knownGaps` skips reading the
 * ingredients and steps (the server caches them: they are most of the bytes).
 */
export function toReviewRow(
  id: string,
  scope: ReviewScope,
  doc: Record<string, unknown>,
  knownGaps?: ContentGap[],
): ReviewRow {
  const source: ReviewSource = scope === 'catalogue' ? 'catalogue' : sourceOfSaved(doc);
  const gaps = knownGaps ?? contentGaps(doc as Parameters<typeof contentGaps>[0]);
  const row: ReviewRow = {
    id,
    scope,
    source,
    state: 'to-check',
    gaps,
    title: str(doc.title) ?? id,
    image: str(doc.image),
    sourceUrl: str(doc.sourceUrl),
    importBatchTitle: str(doc.importBatchTitle),
    importedVia: str(doc.importedVia) ?? str(doc.source),
    savedAt: str(doc.savedAt),
    reviewStatus: str(doc.reviewStatus),
    cuisines: arr(doc.cuisines),
    dishTypes: arr(doc.dishTypes),
    diets: arr(doc.diets),
    metadataSuggestion: doc.metadataSuggestion as MetadataSuggestion | undefined,
    metadataReview: doc.metadataReview as MetadataReview | undefined,
    readyInMinutes: num(doc.readyInMinutes),
    preparationMinutes: num(doc.preparationMinutes),
    cookingMinutes: num(doc.cookingMinutes),
    timeSource: isTimeSource(doc.timeSource) ? doc.timeSource : undefined,
    handsOnMinutes: num(doc.handsOnMinutes),
    timelineStatus: isTimelineStatus(doc.timelineStatus) ? doc.timelineStatus : undefined,
    timelineSuggestion: doc.timelineSuggestion as RecipeTimeline | undefined,
    timelineChecks: Array.isArray(doc.timelineChecks) ? doc.timelineChecks.filter((c): c is string => typeof c === 'string') : undefined,
  };
  return withState(row);
}

/** The row with its state recomputed: after any local change to its tags or status. */
export function withState(row: ReviewRow): ReviewRow {
  return { ...row, state: rowState(row, row.source, row.gaps) };
}

const SOURCE_ORDER: Record<ReviewSource, number> = { import: 0, library: 1, catalogue: 2 };

/** New imports first (newest import first), then the library, then the catalogue, by title. */
export function compareRows(a: ReviewRow, b: ReviewRow): number {
  const bySource = SOURCE_ORDER[a.source] - SOURCE_ORDER[b.source];
  if (bySource !== 0) return bySource;
  if (a.source === 'import') {
    const byDate = (b.savedAt ?? '').localeCompare(a.savedAt ?? '');
    if (byDate !== 0) return byDate;
    const byBatch = (a.importBatchTitle ?? '').localeCompare(b.importBatchTitle ?? '');
    if (byBatch !== 0) return byBatch;
  }
  return a.title.localeCompare(b.title) || a.id.localeCompare(b.id);
}

export interface ReviewPageRequest {
  filter: ReviewFilter;
  source: ReviewSourceFilter;
  offset: number;
  pageSize?: number;
}

export interface ReviewCounts {
  /** Rows under each filter, for the chosen source. */
  filters: Record<ReviewFilter, number>;
  /** Rows under the chosen filter, per source. */
  sources: Record<ReviewSourceFilter, number>;
}

export interface ReviewPage {
  counts: ReviewCounts;
  rows: ReviewRow[];
  nextOffset: number | null;
}

export function countRows(rows: ReviewRow[], filter: ReviewFilter, source: ReviewSourceFilter): ReviewCounts {
  const filters = Object.fromEntries(REVIEW_FILTERS.map((f) => [f, 0])) as Record<ReviewFilter, number>;
  const sources = Object.fromEntries(REVIEW_SOURCE_FILTERS.map((s) => [s, 0])) as Record<ReviewSourceFilter, number>;
  for (const row of rows) {
    const f = filterOf(row.state);
    if (source === 'all' || row.source === source) filters[f]++;
    if (f === filter) {
      sources.all++;
      sources[row.source]++;
    }
  }
  return { filters, sources };
}

export function buildReviewPage(rows: ReviewRow[], request: ReviewPageRequest): ReviewPage {
  const pageSize = Math.max(1, Math.min(request.pageSize ?? REVIEW_PAGE_SIZE, REVIEW_PAGE_SIZE));
  const offset = Math.max(0, Math.floor(request.offset) || 0);
  const matching = rows
    .filter((r) => filterOf(r.state) === request.filter && (request.source === 'all' || r.source === request.source))
    .sort(compareRows);
  const page = matching.slice(offset, offset + pageSize);
  return {
    counts: countRows(rows, request.filter, request.source),
    rows: page,
    nextOffset: offset + pageSize < matching.length ? offset + pageSize : null,
  };
}

export function sanitizePageRequest(raw: Record<string, unknown>): ReviewPageRequest {
  const filter = REVIEW_FILTERS.includes(raw.filter as ReviewFilter) ? (raw.filter as ReviewFilter) : 'to-check';
  const source = REVIEW_SOURCE_FILTERS.includes(raw.source as ReviewSourceFilter) ? (raw.source as ReviewSourceFilter) : 'all';
  const offset = typeof raw.offset === 'number' && Number.isFinite(raw.offset) ? raw.offset : 0;
  return { filter, source, offset };
}
